import React from 'react';
import { FormControl, Select, MenuItem, SelectChangeEvent, SxProps } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';

export type BglOption<TValue = string> = { value: TValue; label: string };

type BglSelectProps<TValue = string> = {
  value: TValue | '' | undefined;
  onChange: (value: TValue | '') => void;
  options: Array<BglOption<TValue>>;
  placeholder?: string;
  width?: number;
  minWidth?: number;
  disabled?: boolean;
  loading?: boolean;
  size?: 'small' | 'medium';
  sort?: boolean;
  sx?: SxProps;
  menuSx?: SxProps;
  inputPaddingY?: number; // control input vertical padding
};

export function BglSelect<TValue = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  width,
  minWidth = 180,
  disabled,
  loading,
  size = 'small',
  sort = false,
  sx,
  menuSx,
  inputPaddingY = 1.1,
}: BglSelectProps<TValue>) {
  const theme = useTheme();
  const sorted = React.useMemo(
    () => (sort ? [...options].sort((a, b) => a.label.localeCompare(b.label)) : options),
    [options, sort]
  );

  const fieldBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;
  const fieldBorder = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.18)
    : theme.palette.divider;

  return (
    <FormControl size={size} sx={{ width: width ? `${width}px` : undefined, minWidth, ...sx }}>
      <Select
        value={(value as any) ?? ''}
        onChange={(e: SelectChangeEvent) => onChange(e.target.value as any)}
        displayEmpty
        renderValue={(val) => {
          if (val === '' || val === undefined || val === null) {
            return <span style={{ color: theme.palette.text.secondary }}>{placeholder}</span>;
          }
          const match = sorted.find(o => String(o.value) === String(val));
          return match?.label ?? String(val);
        }}
        disabled={disabled || loading}
        sx={{
          borderRadius: 1.25,
          bgcolor: fieldBg,
          color: theme.palette.text.primary,
          boxShadow: 'inset 0 0 0 1px transparent',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: fieldBorder },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.secondary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
          '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
          '& .MuiOutlinedInput-input': { py: inputPaddingY, px: 1.25 },
          '& .MuiSelect-icon': { color: theme.palette.text.secondary },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${fieldBorder}`,
              borderRadius: 1.25,
              boxShadow: theme.shadows[8],
              '& .MuiMenuItem-root': {
                '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                '&.Mui-selected.Mui-focusVisible': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                '&:hover': { bgcolor: theme.palette.action.hover },
              },
              ...(menuSx as any),
            },
          },
        }}
      >
        <MenuItem dense value="" sx={{ color: theme.palette.text.secondary }}>
          {loading ? 'Loading…' : placeholder}
        </MenuItem>
        {sorted.map(opt => (
          <MenuItem dense key={String(opt.value)} value={opt.value as any}>{opt.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
} 